// Unread received-kudos state, derived from kudos-received notifications
// (data.kind === "community.kudos" carrying kudosId). The backend has no
// per-kudos read field; dismissing the notification IS the app's read
// model, so one notifications invalidation updates the You section pill,
// the You tab badge, and the main-nav Community badge together.
//
// A notification is auto-dismissed only when its kudos is present in a
// freshly-fetched feed with a terminal "retracted" status: retracted kudos
// render no card, so the badge would otherwise stick with no way to clear
// it. Mere absence from the feed is never treated as gone: it is ambiguous
// (a stale cache, pagination, or share-to-community kudos still in
// pending_company_review that the recipient cannot see yet).

import { analyticsEvents } from "@healthos/analytics";
import type { api } from "@healthos/schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { communityQueryKeys, fetchCommunity } from "../api/community";
import { dismissNotification, NOTIFICATIONS_QUERY_KEY } from "../api/notifications";
import { analytics } from "../lib/analytics";
import { useNotifications } from "./use-notifications";

const KUDOS_NOTIFICATION_KIND = "community.kudos";

// A retracted kudos renders no card, so an unread notification pointing at
// it can never be cleared by the user. That is the only terminal gone-state
// for kudos (the status enum has no "deleted"); everything else is either
// visible or a transient/hidden state we must not auto-dismiss on.
type KudosStatus = api.CommunityKudosFeedItem["status"];

function kudosStatusById(
  feed: readonly api.CommunityFeedItem[] | undefined,
): Map<string, KudosStatus> | null {
  if (!feed) return null;
  const byId = new Map<string, KudosStatus>();
  for (const item of feed) {
    if (item.type === "kudos") byId.set(item.id, item.status);
  }
  return byId;
}

function unreadFromNotifications(
  notifications: readonly api.NotificationItem[] | undefined,
): Map<string, string> {
  const unreadByKudosId = new Map<string, string>();
  for (const notification of notifications ?? []) {
    if (notification.dismissedAt !== null) continue;
    if (notification.data?.kind !== KUDOS_NOTIFICATION_KIND) continue;
    // kudosId rides the schema's passthrough, so it is untyped on the wire.
    const kudosId = notification.data.kudosId;
    if (typeof kudosId === "string" && kudosId.length > 0) {
      unreadByKudosId.set(kudosId, notification.id);
    }
  }
  return unreadByKudosId;
}

export function useUnreadKudos(opts?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const query = useNotifications(
    opts?.enabled === undefined ? undefined : { enabled: opts.enabled },
  );
  // Observe the feed cache without forcing a fetch; CommunityPage (and
  // other surfaces) own loading. Orphan cleanup revalidates before dismiss.
  const feedQuery = useQuery({
    queryKey: communityQueryKeys.feed,
    queryFn: ({ signal }) => fetchCommunity(undefined, signal),
    enabled: false,
  });
  const cachedStatus = useMemo(() => kudosStatusById(feedQuery.data?.feed), [feedQuery.data?.feed]);
  // Badge and You pills follow notifications alone. Filtering against a
  // possibly-stale feed would hide legitimate new kudos until refetch.
  const unreadByKudosId = unreadFromNotifications(query.data);

  const dismissingRef = useRef(new Set<string>());
  useEffect(() => {
    // Wait until a feed has been observed at least once before cleaning up.
    if (!cachedStatus) return;
    const currentUnread = unreadFromNotifications(query.data);
    // Suspect anything the cache says is retracted or does not know about.
    // Absence is only a trigger to revalidate, not grounds to dismiss.
    const suspects: Array<{ kudosId: string; notificationId: string }> = [];
    for (const [kudosId, notificationId] of currentUnread) {
      if (dismissingRef.current.has(notificationId)) continue;
      const status = cachedStatus.get(kudosId);
      if (status === undefined || status === "retracted") {
        suspects.push({ kudosId, notificationId });
      }
    }
    if (suspects.length === 0) return;

    let cancelled = false;
    void (async () => {
      let freshStatus: Map<string, KudosStatus> | null;
      try {
        const fresh = await queryClient.fetchQuery({
          queryKey: communityQueryKeys.feed,
          queryFn: ({ signal }) => fetchCommunity(undefined, signal),
        });
        if (cancelled) return;
        freshStatus = kudosStatusById(fresh.feed);
      } catch {
        return;
      }
      if (!freshStatus) return;

      // Dismiss only kudos the fresh feed confirms are retracted. Absent
      // kudos stay untouched: they may be hidden by moderation or paging.
      const confirmedStatus = freshStatus;
      const orphans = suspects
        .filter(({ kudosId }) => confirmedStatus.get(kudosId) === "retracted")
        .map(({ notificationId }) => notificationId);
      if (orphans.length === 0) return;

      for (const notificationId of orphans) {
        dismissingRef.current.add(notificationId);
        try {
          await dismissNotification(notificationId);
        } catch {
          dismissingRef.current.delete(notificationId);
        }
      }
      if (!cancelled) {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query.data, cachedStatus, queryClient]);

  return { unreadByKudosId, count: unreadByKudosId.size, isPending: query.isPending };
}

export function useMarkKudosRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { notificationId: string; kudosId: string }) => {
      await dismissNotification(input.notificationId);
    },
    onSuccess: () => {
      analytics.track(analyticsEvents.communityKudosRead, {});
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}
